"use client";

import type { CaseListData } from "@repo/provider-network";
import { useInitialContext } from "@repo/sdk";
import { ensureAuthCookie } from "@repo/session/auth-cookie";
import { DynamicModal } from "@repo/ui";
import {
  type RegisteredStatsigEvent,
  useScreenViewed,
  useStatsigClient,
} from "@repo/statsig";
import { useGateValue } from "@statsig/react-bindings";
import { useRouter } from "next/navigation";
import { useEffect, useReducer, useState, useTransition } from "react";
import BlockVisitModalContent from "src/app/block-visit/BlockVisitModalContent";
import { MenuModal } from "src/app/home/[flagCode]/components/menu/menu-modal";
import StartVisitButtonFooter from "src/app/home/[flagCode]/components/start-visit-button-footer";
import VisitList from "src/app/home/[flagCode]/components/visit-list";
import { VisitListGate } from "src/app/home/[flagCode]/components/visit-list-gate";
import { useRevalidateSubscriptionOnVisible } from "src/app/home/[flagCode]/components/hooks/use-revalidate-subscription-on-visible";
import { getSurveyEligibility } from "src/app/nps/actions";
import { NpsDrawer } from "src/app/nps/components";
import { useNpsCooldown } from "src/app/nps/hooks/use-nps-cooldown";
import {
  checkForActiveVisit,
  confirmVisitRequest,
} from "src/app/select-patient/actions";
import { PatientSelectionContent } from "src/app/select-patient/components";
import { ListPageLayout } from "src/components/list-page-layout";
import { KinsaInitialContext, Patient } from "src/lib/definitions";
import { filterChildrenUnder21, isInFuture } from "src/lib/member-utils";
import { Routes } from "src/lib/routes";
import { useSubscriptionCheck } from "src/lib/subscription-gate";
import { reportConfirmVisitError } from "../report-confirm-visit-error";
import type { HomeState } from "../../types";

import PaymentPage from "src/app/home/steps/payment/page";
import { PaywallContent as KinsaPaywallContent } from "src/app/paywall/two-prod/components";
import { PaymentFlow, PaymentSuccess } from "src/components/PaymentFlow";
import { CreatingPatientPageSkeleton } from "src/components/skeletons/PageSkeleton";
import { useTabBarVisibility } from "@repo/sdk/publisher";
import { usePaywallCompletion } from "src/app/home/[flagCode]/components/hooks/use-paywall-completion";
import { advanceThroughGates, homeReducer } from "../reducer";
import { sdkSupportsTabBar } from "@repo/sdk/publisher";

interface KinsaHomeWizardProps {
  initialState: HomeState;
  initialVisitData: CaseListData | null;
}

export default function KinsaHomeWizard({
  initialState,
  initialVisitData,
}: KinsaHomeWizardProps) {
  const router = useRouter();
  const initialContext = useInitialContext<KinsaInitialContext>();
  const { logEvent } = useStatsigClient();

  // URL overrides (computed once on mount)
  const resolvedInitialState: HomeState = initialState.config
    .preselectedPatientId
    ? advanceThroughGates(initialState, initialState.config.hasSubscription)
    : initialState;

  const [state, dispatch] = useReducer(homeReducer, resolvedInitialState);
  const [isConfirmingPatient, startConfirmTransition] = useTransition();

  // SDK context is trusted (provided by native app container);
  // cookie enables server-side account resolution via resolveAccount().
  useEffect(() => {
    if (initialContext?.account?.id) {
      ensureAuthCookie(initialContext.account.id);
    }
  }, [initialContext?.account?.id]);

  useScreenViewed(state.step);

  // Auto-navigate to intake when preselected patient reaches navigating-to-intake.
  // Checks for active visits first (which the old auto-select flow skipped).
  useEffect(() => {
    if (state.step !== "navigating-to-intake") return;

    const patients = initialContext?.account?.coveredPersons ?? [];
    const patient = patients.find(
      (p) => p.id === state.config.preselectedPatientId
    );
    if (!patient) {
      dispatch({ type: "SELECT_PATIENT_CANCEL" });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const hasActive = await checkForActiveVisit(patient);
        if (cancelled) return;

        if (hasActive) {
          dispatch({
            type: "BLOCK_VISIT",
            patient: {
              id: patient.id,
              firstName: patient.firstName,
              profileImage: patient.custom?.profileImage,
            },
          });
        } else {
          router.push(`/intake/${patient.id}`);
        }
      } catch (err) {
        if (cancelled) return;
        if (reportConfirmVisitError(err, logEvent)) {
          dispatch({ type: "SELECT_PATIENT_CANCEL" });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.step, state.config.preselectedPatientId, initialContext]);

  // --- Payment handlers (outside reducer — they navigate away) ---

  const { expressPhase, handleCheckoutComplete, handlePaymentError } =
    usePaywallCompletion(() => dispatch({ type: "PAYWALL_COMPLETE" }));

  // --- Modal / overlay state (orthogonal to step machine) ---

  // TODO(sid-review): useSubscriptionCheck is also called inside ActiveStartVisitButtonFooter —
  // pass subscriptionState as a prop to eliminate the redundant hook call.
  const { subscriptionState, mutate: mutateSubscription } =
    useSubscriptionCheck();

  useRevalidateSubscriptionOnVisible(
    mutateSubscription,
    state.subscriptionJustCompleted
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // When we are in visit-list and the menu is not open make the TabBar visible, otherwise always hide
  useTabBarVisibility(
    state.step === "visit-list" && !isMenuOpen ? "visible" : "hidden"
  );

  // NPS state
  const [isNpsOpen, setIsNpsOpen] = useState(false);
  const [npsEligible, setNpsEligible] = useState<boolean | null>(null);
  const showNpsSurveyGate = useGateValue("show_nps_survey");
  const { setCooldown } = useNpsCooldown();

  useEffect(() => {
    if (state.step !== "visit-list" || !showNpsSurveyGate) return;
    let cancelled = false;
    getSurveyEligibility()
      .then((eligible) => {
        if (!cancelled) setNpsEligible(eligible);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.step, showNpsSurveyGate]);

  const shouldShowNps =
    state.step === "visit-list" && showNpsSurveyGate && npsEligible === true;
  useEffect(() => {
    if (shouldShowNps) {
      const timer = setTimeout(() => setIsNpsOpen(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowNps]);

  const handleMenu = () => {
    logEvent({ eventName: "menu_opened" } as RegisteredStatsigEvent);
    setIsMenuOpen(true);
  };

  const handleNpsClose = () => {
    setCooldown();
    setIsNpsOpen(false);
  };

  const patients = initialContext?.account?.coveredPersons || [];
  const eligibleChildren = filterChildrenUnder21(patients);

  // --- Dispatch wrappers ---

  const handleStartVisit = () => {
    // Auto-select when only one person on the account
    if (patients.length === 1) {
      const candidate = patients[0];

      // Unborn child guard
      if (candidate.birthDate && isInFuture(candidate.birthDate)) {
        dispatch({
          type: "UNBORN_CHILD_WARNING",
          childName: candidate.firstName,
        });
        return;
      }

      // Client-side check: active/requested visit based on initial data
      if (initialVisitData) {
        const allVisits = [
          ...(initialVisitData.current || []),
          ...(initialVisitData.past || []),
        ];
        const hasActive = allVisits.some((visit) => {
          const status = visit.case_status?.name?.toLowerCase();
          return (
            String(visit.patient_id) === String(candidate.id) &&
            (status === "requested" || status === "active")
          );
        });
        if (hasActive) {
          dispatch({
            type: "BLOCK_VISIT",
            patient: {
              id: candidate.id,
              firstName: candidate.firstName,
              profileImage: candidate.custom?.profileImage,
            },
          });
          return;
        }
      }

      confirmVisitRequest(candidate).catch((err) => {
        reportConfirmVisitError(err, logEvent);
      });
      return;
    }

    dispatch({
      type: "START_VISIT",
      eligibleChildrenCount: eligibleChildren.length,
    });
  };

  const handleSelectPatientConfirm = (patient: Patient) => {
    if (isConfirmingPatient) return;
    startConfirmTransition(async () => {
      try {
        const hasActive = await checkForActiveVisit(patient);
        if (hasActive) {
          dispatch({
            type: "BLOCK_VISIT",
            patient: {
              id: patient.id,
              firstName: patient.firstName,
              profileImage: patient.custom?.profileImage,
            },
          });
          return;
        }

        confirmVisitRequest(patient).catch((err) => {
          if (reportConfirmVisitError(err, logEvent)) {
            dispatch({ type: "SELECT_PATIENT_CANCEL" });
          }
        });
      } catch (err) {
        if (reportConfirmVisitError(err, logEvent)) {
          dispatch({ type: "SELECT_PATIENT_CANCEL" });
        }
      }
    });
  };

  const handleBlockVisit = (patient: Patient) => {
    dispatch({
      type: "BLOCK_VISIT",
      patient: {
        id: patient.id,
        firstName: patient.firstName,
        profileImage: patient.custom?.profileImage,
      },
    });
  };

  // --- Full-screen steps ---

  // Express Checkout redirect — show spinner while processing, success screen after.
  if (expressPhase === "processing") {
    return <CreatingPatientPageSkeleton />;
  }
  if (expressPhase === "success") {
    return <PaymentSuccess />;
  }

  if (state.step === "paywall") {
    return (
      <PaymentFlow
        returnPath={Routes.HOME}
        paywallComponent={KinsaPaywallContent}
        onComplete={() => dispatch({ type: "PAYWALL_COMPLETE" })}
        onError={handlePaymentError}
        onDismiss={
          state.paywallDismissable
            ? () => dispatch({ type: "DISMISS_PAYWALL" })
            : undefined
        }
      />
    );
  }

  if (state.step === "checkout") {
    return (
      <PaymentPage
        onBack={() => dispatch({ type: "BACK_TO_PAYWALL" })}
        onPaymentComplete={handleCheckoutComplete}
      />
    );
  }

  if (state.step === "navigating-to-intake") {
    return <CreatingPatientPageSkeleton />;
  }

  // --- Steps with visit-list layout background ---

  const selectedPatient = initialContext?.account?.selectedPatient || null;
  const hasVisits =
    initialVisitData &&
    (initialVisitData.current?.length || 0) +
      (initialVisitData.past?.length || 0) >
      0;

  const isVisitListStep = state.step === "visit-list";

  return (
    <>
      <ListPageLayout
        navHeaderProps={{
          leftButtonType: "menu",
          showCloseButton: !sdkSupportsTabBar(initialContext?.app?.sdkVersion),
          onLeftClick: handleMenu,
        }}
      >
        <VisitListGate>
          {isVisitListStep ? (
            <>
              <VisitList
                initialData={initialVisitData ?? undefined}
                hasTerms={state.config.hasTerms}
                className="pb-40"
                showEmptyState={true}
                onStartVisit={handleStartVisit}
                subscriptionState={subscriptionState}
                onExplorePlans={() => {
                  const hasSub =
                    state.config.hasSubscription ||
                    state.subscriptionJustCompleted;
                  if (hasSub) {
                    setIsMenuOpen(true);
                  } else {
                    dispatch({ type: "SHOW_PAYWALL" });
                  }
                }}
                hasSubscription={
                  state.config.hasSubscription ||
                  state.subscriptionJustCompleted
                }
              />
              <StartVisitButtonFooter
                hasVisits={!!hasVisits}
                onManagePayment={() => dispatch({ type: "MANAGE_PAYMENT" })}
              />
            </>
          ) : (
            <StartVisitButtonFooter disabled />
          )}
        </VisitListGate>
      </ListPageLayout>

      {/* Select patient modal */}
      {state.step === "select-patient" && (
        <DynamicModal
          isOpen={true}
          onClose={() => dispatch({ type: "SELECT_PATIENT_CANCEL" })}
          dismissible={true}
          size="auto"
        >
          <PatientSelectionContent
            patients={patients}
            initialSelectedPatient={selectedPatient || undefined}
            onConfirm={handleSelectPatientConfirm}
            onCancel={() => dispatch({ type: "SELECT_PATIENT_CANCEL" })}
            onBlockVisit={handleBlockVisit}
          />
        </DynamicModal>
      )}

      {/* Block visit modal */}
      {state.step === "block-visit" && state.blockVisitPatient && (
        <DynamicModal
          isOpen={true}
          onClose={() => dispatch({ type: "BLOCK_VISIT_DISMISS" })}
          dismissible={true}
          size="auto"
        >
          <BlockVisitModalContent
            patientData={state.blockVisitPatient}
            onDismiss={() => dispatch({ type: "BLOCK_VISIT_DISMISS" })}
          />
        </DynamicModal>
      )}

      {/* Menu modal */}
      <MenuModal
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        hasSubscription={
          state.config.hasSubscription || state.subscriptionJustCompleted
        }
        onSeePlans={() => {
          setIsMenuOpen(false);
          dispatch({ type: "SHOW_PAYWALL" });
        }}
      />

      {/* NPS survey */}
      <NpsDrawer isOpen={isNpsOpen} onClose={handleNpsClose} />
    </>
  );
}
